import { useQuery } from '@tanstack/react-query';

import { t } from '../../../lib/i18n';
import { fetchBidAudit } from '../api';
import { type AuditEntry } from '../types';
import { cn, formatClock, formatShortDate } from '../utils';

/** Query key prefix of the per-bid audit trail. */
export const BID_AUDIT_QUERY_KEY = ['bids', 'audit'] as const;

/** Newest first, whatever order the wire delivered — defensive on top of the API's own sort. */
export function sortNewestFirst(entries: AuditEntry[]): AuditEntry[] {
  return [...entries].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

/**
 * Payload keys worth a glance in one line, in the order an operator reads
 * them. Everything else stays behind the row's `title` (the raw JSON).
 */
const SUMMARY_KEYS = [
  'newStatus',
  'outcome',
  'upstreamRef',
  'upstreamAmount',
  'rejectReason',
  'rejectNote',
  'reason',
  'relayCancelReason',
  'relayRejectReason',
  'releaseReason',
  'force',
  'claimedBy',
  'newMinimum',
  'cancelledCount',
  'winningAmount',
  'bidAmount',
] as const;

/** One-line digest of an audit payload: the relay move first, then the named fields that are set. */
export function summarizePayload(data: Record<string, unknown>): string {
  const parts: string[] = [];

  const from = data.previousRelayStatus;
  const to = data.newRelayStatus;
  if (typeof from === 'string' && typeof to === 'string' && from !== to) {
    parts.push(`${from} → ${to}`);
  }

  for (const key of SUMMARY_KEYS) {
    const value = data[key];
    if (value === undefined || value === null || value === '' || value === false) continue;
    parts.push(`${key}: ${typeof value === 'object' ? JSON.stringify(value) : String(value)}`);
  }

  return parts.join(' · ');
}

/**
 * «Історія» — the operator trail of one bid (Phase 13), read from
 * `GET /api/admin/audit?bidId=`. The outbox is already the audit log (every
 * relay move, claim, admin control and client withdrawal is appended in the
 * same transaction as the write), so this tab only reads it back: type, actor,
 * time and a payload digest, newest first.
 */
export function BidHistoryTab({ bidId }: { bidId: string }) {
  const query = useQuery({
    queryKey: [...BID_AUDIT_QUERY_KEY, bidId],
    queryFn: () => fetchBidAudit(bidId),
    enabled: Boolean(bidId),
  });

  if (query.isLoading) {
    return (
      <p
        data-testid="bid-history-loading"
        className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center text-[13px] text-cf-ink-subtle"
      >
        {t('bids.history.loading')}
      </p>
    );
  }

  if (query.isError) {
    return (
      <section
        data-testid="bid-history-error"
        className="rounded-cf-lg border border-cf-line bg-cf-surface p-10 text-center"
      >
        <p className="text-base font-semibold">{t('bids.history.error')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="mt-4 h-10 rounded-cf-md bg-cf-brand px-4 text-sm font-semibold text-white hover:bg-cf-brand-hover"
        >
          {t('bids.history.retry')}
        </button>
      </section>
    );
  }

  const entries = sortNewestFirst(query.data ?? []);

  if (entries.length === 0) {
    return (
      <p
        data-testid="bid-history-empty"
        className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center text-[13px] text-cf-ink-subtle"
      >
        {t('bids.history.empty')}
      </p>
    );
  }

  return (
    <section
      data-testid="bid-history"
      className="overflow-x-auto rounded-cf-lg border border-cf-line bg-cf-surface"
    >
      <div className="grid min-w-[720px] grid-cols-[220px_160px_140px_minmax(0,1fr)] gap-3.5 border-b border-cf-line-soft px-[18px] py-2.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase">
        <span>{t('bids.history.columns.event')}</span>
        <span>{t('bids.history.columns.actor')}</span>
        <span>{t('bids.history.columns.time')}</span>
        <span>{t('bids.history.columns.details')}</span>
      </div>
      <ol className="m-0 list-none p-0">
        {entries.map((entry) => (
          <li
            key={entry.id}
            data-testid="bid-history-row"
            className="grid min-w-[720px] grid-cols-[220px_160px_140px_minmax(0,1fr)] items-start gap-3.5 border-b border-cf-line-soft px-[18px] py-3 text-[13px] last:border-b-0"
          >
            <span className="truncate font-mono text-xs font-semibold" title={entry.type}>
              {entry.type}
            </span>
            {/* «Хто»: the admin's MASKED email (resolved server-side, the same
                masking as the relay queue's claimer badge); the raw id only
                when the API could not resolve it, and always in the title. */}
            <span
              className={cn('truncate', entry.who ? 'font-mono text-xs' : 'text-cf-ink-subtle')}
              title={entry.who ?? undefined}
            >
              {entry.who ? entry.whoLabel || entry.who : t('bids.history.system')}
            </span>
            <span className="font-mono text-xs text-cf-ink-muted whitespace-nowrap">
              {`${formatShortDate(entry.at)} ${formatClock(entry.at)}`.trim()}
            </span>
            <span
              className="min-w-0 break-words text-cf-ink-muted"
              title={JSON.stringify(entry.data)}
            >
              {summarizePayload(entry.data) || '—'}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
